<template>
  <form
    class="template notification-center-page__form"
    v-on:submit.prevent="onSubmit"
  >
    <input-text
      class="template__name"
      maxlength="100"
      minlength="3"
      placeholder="Название рассылки"
      required="required"
      v-bind:title="'Название'"
      v-model="name"
      v-bind:violations="nameInputErrors"
    />
    <the-select
      class="template__event"
      required="required"
      v-bind:option-title-key="'name'"
      v-bind:option-value-key="'id'"
      v-bind:options="events"
      v-bind:placeholder="'Выберите событие'"
      v-bind:title="'Событие'"
      v-bind:violations="eventInputErrors"
      v-model="eventId"
      v-on:input="onChangeEvent" />
    <span v-if="eventId === $options.eventBirthDate">
      Отправка производится ежедневно в 12 часов дня.
    </span>
    <the-select
      v-if="showDispatchTimeSelector"
      class="template__event"
      required="required"
      v-bind:options="dispatchTimes.toArray()"
      v-bind:placeholder="'Выберите время отправки'"
      v-bind:title="'Время отправки'"
      v-bind:violations="dispatchTimeInputErrors"
      v-model="dispatchTime" />
    <date-time-picker
      required="required"
      v-if="eventId === $options.eventByDate"
      title="Выберите дату и время отправки"
      class="notification-center-page__date-time-select"
      sub-title="Дата и время отправки"
      v-bind:with-minutes="false"
      v-bind:initial-date="dispatchTime"
      v-bind:violations="dispatchTimeInputErrors"
      v-on:submit="onChangeTime"
    />
    <the-select
      class="template__event"
      option-title-key="name"
      option-value-key="value"
      required="required"
      title="Канал"
      placeholder="Каналы отправки"
      v-bind:options="channels"
      v-bind:violations="channelsInputErrors"
      v-model="selectedChannel" />
    <div
      v-if="eventId === $options.eventByDate"
      class="notification-center-page__switch"
    >
      <v-switch
        v-model="repeatable"
      />
      <span>Повторяемый</span>
    </div>
    <template v-if="hasFragments">
      <textarea-length-limitation
        v-bind:value="roughValue"
        v-on:input="onUpdateRoughValue"
        v-on:cursor-position="onUpdateCursorPositions"
        v-bind:maxLength="400"
        ref='textarea'
        placeholder="Текст сообщения"
        v-bind:violations="fragmentsInputErrors"
      />
      <div
        v-if="variables.length"
        class="notification-center-page__chips">
        <v-chip
          v-for="variable in variables"
          v-bind:key="variable.id"
        >
          <span>{{ variable.id }}</span>
        </v-chip>
      </div>
      <add-fragment
        class="template__add-fragment"
        v-bind:types="fragmentTypesWithoutText"
        v-on:input="onAddFragment"
      />
    </template>
    <div
      v-if="shownOptions"
      class="notification-center-page__recipients">
      <h4>
        {{ descriptionNotifiablePatients ? `Получатели (${descriptionNotifiablePatients})` : 'Получатели' }}
        <span v-if="showCountNotifiablePatients">
          : {{ countNotifiablePatients }}
        </span>
      </h4>
      <the-select
        class="template__event"
        required="required"
        option-title-key="title"
        option-value-key="value"
        v-bind:clearable="true"
        v-bind:options="sexOptions"
        placeholder="Не выбрано"
        title="Пол"
        v-model="sexId"
        v-on:input="onChangeOptions"
      />
      <div class="notification-center-page__recipients__date recipients-date">
        <span>Выберите дату последнего визита</span>
        <date-picker-dialog
          range
          v-bind:value="period"
          v-on:input="onInputPeriod"
          v-on:close="onCloseDatePickerDialog"
        >
          <template v-slot:trigger="{ listenerObject }">
            <button
              v-if="period.length > 0"
              class="recipients-date__button"
              type="button"
              v-on="listenerObject"
            >
              {{ periodTitle }}
            </button>
            <button
              v-else
              class="recipients-date__button-calendar"
              type="button"
              v-on="listenerObject"
            />
          </template>
        </date-picker-dialog>
      </div>
      <autocomplete-input
        item-text="title"
        item-value="value"
        label="Услуга"
        v-bind:menu-props="{ maxWidth: 'min(80%, 586px)' }"
        v-bind:items="serviceOptions"
        v-bind:violations="serviceInputErrors"
        v-bind:clearable="true"
        v-model="service"
        v-on:input="onChangeOptions"
        solo />
      <the-select
        v-bind:clearable="true"
        placeholder="Выберите воронку"
        v-bind:options="funnelOptions"
        v-model="funnel"
        v-on:input="handleFunnelInput"
        v-bind:violations="funnelInputErrors" />
      <the-select
        v-bind:clearable="true"
        v-if="funnel"
        placeholder="Выберите этап"
        v-bind:options="funnelStageOptions"
        v-model="stage"
        v-on:input="onChangeOptions"
        v-bind:violations="stageInputErrors" />
    </div>
    <button
      class="template__submit"
      type="submit"
    >Сохранить</button>
  </form>
</template>
<script>
import { List, Maybe } from 'monet';
import notifierMixin from '@/mixins/notifierMixin';
import AddFragment from '@/components/NotificationPlan/AddFragment.vue';
import InputText from '@/components/InputText.vue';
import TheSelect from '@/components/TheSelect.vue';
import smsTemplateMixin, { templateFragmentComponents } from '@/mixins/notificationPlanMixin';
import { NotificationPlanCreationRequest } from '@/storage/gateways/NotificationPlansGateway';
import DateTimePicker from '@/components/DateTimePicker.vue';
import { formatDate } from '@/utils/timeFormat';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violations ? vm.violations[cause] : []
);

/**
 * @description Форма создания шаблона СМС.
 * @type {Vue}
 */
export default {
  sexMale: 1,
  sexFemale: 2,
  sexNone: 3,
  eventUpcomingVisit: 'upcoming-visit',
  eventPastVisit: 'past-visit',
  eventByDate: 'by-date',
  eventBirthDate: 'birth-date',
  components: {
    AutocompleteInput,
    DatePickerDialog,
    DateTimePicker,
    TextareaLengthLimitation,
    AddFragment,
    InputText,
    TheSelect,
    ...templateFragmentComponents,
  },
  computed: {
    nameInputErrors: resolveInputErrors('input_name'),
    fragmentsInputErrors: resolveInputErrors('input_fragments'),
    channelsInputErrors: resolveInputErrors('channels'),
    eventInputErrors: resolveInputErrors('input_event'),
    dispatchTimeInputErrors: resolveInputErrors('input_dispatch_time'),
    serviceInputErrors: resolveInputErrors('input_service'),
    funnelInputErrors: resolveInputErrors('input_funnel'),
    stageInputErrors: resolveInputErrors('input_stage'),
    shownOptions() {
      return this.eventId !== this.$options.eventUpcomingVisit
        && this.eventId !== this.$options.eventPastVisit;
    },
    /**
     * @description Отображаемый заголовок выбранного периода.
     * @return {string}
     */
    periodTitle() {
      const [start, end] = this.period.map((date) => formatDate(date));
      if (!end) {
        return `${start}  —  ${start}`;
      }
      return `${start}  —  ${end}`;
    },
    /**
     * @description Типы фрагментов шаблона.
     * @type {any[]}
     */
    fragmentTypes() {
      return List
        .from(this.events)
        .find(({ id }) => this.eventId === id)
        .bind((event) => Maybe.fromEmpty(event.template_fragments))
        .toList()
        .bind(List.fromArray)
        .filter(this.hasFragmentTypeByName)
        .map(this.mergeWithFragmentParams)
        .toArray();
    },
    dispatchTimes() {
      return List
        .from(this.events)
        .find(({ id }) => this.eventId === id)
        .map(({ times }) => times)
        .toList()
        .bind(List.fromArray);
    },
    /**
     * @description Этапы выбранной воронки.
     */
    funnelStageOptions() {
      return this.stageOptions.flatMap(
        (option) => (this.funnel === option.funnel ? [option] : []),
      );
    },
    /**
     * @description Типы фрагментов шаблона без текста.
     * @type {any[]}
     */
    fragmentTypesWithoutText() {
      return this.fragmentTypes.filter((fragment) => fragment.id !== 'Текст');
    },
    /**
     * @description Имеются фрагменты.
     * @type {boolean}
     */
    hasFragments() {
      return this.fragmentTypes.length > 0;
    },
    /**
     * @description Позиция последнего фрагмента.
     * @type {number}
     */
    lastPosition() {
      return Maybe
        .fromUndefined(this.fragments.slice(-1).shift())
        .cata(() => 0, (item) => item.position);
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      name: vm.name,
      fragments: vm.fragments,
      eventId: vm.eventId,
      selectedChannel: vm.selectedChannel,
      dispatchTime: vm.dispatchTime,
      period: vm.period,
      sexId: vm.sexId,
      repeatable: vm.repeatable,
      service: vm.service,
      funnel: vm.funnel,
      stage: vm.stage,
    }),
    variables() {
      return this.fragments.filter((fragment) => fragment.id !== 'Текст');
    },
    /**
     * @description Отображать селектор выбора даты отправки.
     * @returns {boolean}
     */
    showDispatchTimeSelector() {
      return this.dispatchTimes.size() > 0
        && (
          this.eventId === this.$options.eventUpcomingVisit
            || this.eventId === this.$options.eventPastVisit
        );
    },
    /**
     * @description Отображать количество уведомляемых пользователей.
     * @returns {boolean}
     */
    showCountNotifiablePatients() {
      return this.eventId !== this.$options.eventUpcomingVisit
        && this.eventId !== this.$options.eventPastVisit;
    },
  },
  created() {
    if (this.initialEvent === '') {
      this
        .events
        .slice(0, 1)
        .forEach(({ id }) => { this.eventId = id; });
    }
    if (this.initialChannel.length === 0) {
      this
        .channels
        .slice(0, 1)
        .forEach((item) => { this.selectedChannel = item.value; });
    }
    this.onChangeOptions();
  },
  data() {
    return {
      cursorPosition: 0,
      eventId: this.initialEvent,
      sexId: this.initialSexId,
      fragments: this.initialFragments,
      name: this.initialName,
      roughValue: this.getStringByFragments(this.initialFragments),
      patients: [],
      selectedPatients: [],
      period: this.initialPeriod,
      selectedChannel: this.initialChannel,
      dispatchTime: this.initialDispatchTime,
      repeatable: this.initialRepeatable,
      service: this.initialService,
      funnel: this.initialFunnel,
      stage: this.initialStage,
      sexOptions: [
        {
          title: 'Мужской',
          value: this.$options.sexMale,
        },
        {
          title: 'Женский',
          value: this.$options.sexFemale,
        },
        {
          title: 'Не указан',
          value: this.$options.sexNone,
        },
      ],
    };
  },
  emptyEventId: '',
  methods: {
    /**
     * @description Очистить параметры опций.
     * @returns {void}
     */
    clearOptions() {
      this.sexId = '';
      this.period = [];
      this.repeatable = false;
      this.service = '';
      this.funnel = '';
      this.stage = '';
      if (this.eventId === this.$options.eventByDate) {
        this.dispatchTime = new Date().toISOString();
      } else {
        this.dispatchTime = '';
      }
    },
    /**
     * @description Обработать закрытие диалога выборы даты.
     * @returns {void}
     */
    onCloseDatePickerDialog() {
      if (this.period.length === 1) {
        const [left] = this.period;
        this.onInputPeriod([left, left]);
      }
    },
    /**
     * @description Обработать изменение периода.
     * @param {Date[]} value
     * @returns {void}
     */
    onInputPeriod(value) {
      this.period = value.sort(
        (first, second) => Number(first.getTime() > second.getTime()),
      );
      this.onChangeOptions();
    },
    /**
     * @description Обработать изменение опций плана.
     * @returns {void}
     */
    onChangeOptions() {
      const request = this.makeRequest();
      this.$emit('change-options', request);
    },
    /**
     * @description Обработать изменение события.
     * @returns {void}
     */
    onChangeEvent() {
      this.clearOptions();
      this.onChangeOptions();
    },
    /**
     * @description Обработать изменения времени отправки
     * @param {string} time
     */
    onChangeTime(time) {
      this.dispatchTime = time;
    },
    /**
     * @description Обработать добавление фрагмента.
     * @param {any} fragment Фрагмент.
     * @returns {void}
     */
    onAddFragment(fragment) {
      const positionableFragment = {
        position: this.lastPosition + 1,
        ...fragment,
      };
      this.cursorPosition = Math.min(this.cursorPosition, this.roughValue.length);
      const before = this.roughValue.slice(0, this.cursorPosition);
      const after = this.roughValue.slice(this.cursorPosition);
      this.roughValue = `${before}{${fragment.name}} ${after}`;
      this.setFragments([...this.fragments, positionableFragment]);
      this.onUpdateRoughValue(this.roughValue);
      this.$nextTick(() => {
        const textarea = this.$refs.textarea.$el.querySelector('textarea');
        if (textarea) {
          this.cursorPosition += `{${fragment.name}} `.length;
        }
      });
    },
    /**
     * @description Обработать отправку данных шаблона.
     * @returns {void}
     */
    onSubmit() {
      const request = this.makeRequest();
      this.$emit('submit', request);
    },
    /**
     * @description Прослушка ввода CRM-воронки
     * @returns {void}
     */
    handleFunnelInput() {
      this.resetStage();
      this.onChangeOptions();
    },
    /**
     * @description Подготовить запрос.
     * @returns {NotificationPlanCreationRequest}
     */
    makeRequest() {
      const fragments = this.fragments.map(
        ({ id, position, value = '' }) => ({ id, position, value }),
      );
      return new NotificationPlanCreationRequest(
        this.id,
        this.name,
        fragments,
        this.eventId,
        this.selectedChannel,
        this.dispatchTime,
        this.period,
        this.sexId,
        this.repeatable,
        this.service,
        this.funnel,
        this.stage,
      );
    },
    /**
     * @description Установить фрагменты.
     * @param items
     */
    setFragments(items) {
      const orderBy = ({ position: left }, { position: right }) => left - right;
      this.fragments = [...items]
        .sort(orderBy)
        .map((fragment, index) => ({ ...fragment, position: index + 1 }));
    },
    /**
     * @description Очистить выбранные фрагменты шаблона.
     * @returns {void}
     */
    resetFragments() {
      this.setFragments([]);
    },
    /**
     * @description Сбросить выбор этапа.
     * @returns {void}
     */
    resetStage() {
      this.stage = '';
    },
    /**
     * @description Обновить приблизительное значение.
     * @param {string} value
     */
    onUpdateRoughValue(value) {
      const fragments = value
        .split(/({.*?})/)
        .filter((s) => !!s)
        .map((fragment, index) => {
          if (this.fragmentTypes.some((type) => `{${type.id}}` === fragment)) {
            const result = { ...this.fragmentTypes.find((type) => `{${type.id}}` === fragment) };
            result.position = index + 1;
            return result;
          }
          const result = { ...this.fragmentTypes.find((type) => type.id === 'Текст') };
          result.position = index + 1;
          result.value = fragment;
          return result;
        });
      this.setFragments(fragments);
      this.roughValue = value;
    },
    /**
     * @description Обновить позицию курсора.
     * @param  {number} value
     */
    onUpdateCursorPositions(value) {
      this.cursorPosition = value;
    },
    /**
     * @description Получить строку по фрагментам.
     * @param {array} fragments
     * @returns {string}
     */
    getStringByFragments(fragments) {
      return fragments.map((fragment) => {
        if (fragment.value) return fragment.value;
        return `{${fragment.id}}`;
      }).join('');
    },
  },
  mixins: [
    notifierMixin,
    smsTemplateMixin,
    formMeaningfulFieldsMixin,
  ],
  name: 'FormNotificationPlan',
  props: {
    /**
     * @description Уведомляемые события.
     * @type {any[]}
     */
    events: { type: Array, required: true },
    /**
     * @description Каналы отправки.
     * @type {Array}
     */
    channels: { type: Array, required: true },
    /**
     * @description Количество уведомляемых пациентов.
     * @type {Number}
     */
    countNotifiablePatients: { type: Number, default: () => 0 },
    /**
     * @description Описание к уведомляемым пациентам.
     * @type {String}
     */
    descriptionNotifiablePatients: { type: String, default: () => '' },
    id: { type: String, default: () => null },
    initialName: { type: String, default: () => '' },
    initialFragments: { type: Array, default: () => [] },
    initialEvent: { type: String, default: () => '' },
    initialDispatchTime: { type: String, default: () => '' },
    initialChannel: { type: String, default: () => '' },
    initialSexId: { type: String, default: () => '' },
    initialPeriod: { type: Array, default: () => [] },
    initialRepeatable: { type: Boolean, default: () => false },
    initialService: { type: String, default: () => '' },
    initialFunnel: { type: String, default: () => '' },
    initialStage: { type: String, default: () => '' },
    serviceOptions: { type: Array, default: () => [] },
    funnelOptions: { type: Array, default: () => [] },
    stageOptions: { type: Array, default: () => [] },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => {},
      type: Object,
    },
  },
  watch: {
    eventId() {
      this.resetFragments();
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";

::v-deep .textarea-length-limitation {
  &__textarea {
    min-height: 40px;
    max-height: 220px;
  }
}

.notification-center-page {
  &__form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 25px;
    margin-top: 37px;
  }
  &__date-time-select {
    padding: 0;
  }
  &__switch,
  &__chips {
    display: flex;
    align-items: center;
    gap: 15px;
  }
  &__chips {
    flex-wrap: wrap;
    gap: 10px;
  }
  &__recipients {
    display: grid;
    gap: 18px;
  }

  .recipients-date {
    display: grid;
    gap: 15px;
    &__button {
      width: fit-content;
      color: var(--action-color);
      font-size: 18px;
      font-weight: 500;
      text-align: left;
      line-height: 22px;
      white-space: nowrap;
      border-bottom: 1px dashed;
      cursor: pointer;
      &:hover {
        border-bottom: 1px dashed transparent;
      }
    }
    &__button-calendar {
      width: 25px;
      height: 25px;
      background: url("@/assets/img/calendar.svg") no-repeat 50%;
      background-size: 25px;
    }
  }
}

.template {
  &__add-fragment,
  &__event,
  &__name {
    width: min(100%, 40rem);
  }
  &__fragment {
    cursor: grab;
    width: max(60%, 40rem);
  }
  &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
  }
}

@media screen and (max-width: 480px) {
  .template{
    &__add-fragment {
      display: grid;
      gap: 10px;
    }
    &__submit {
      width: 100%;
    }
  }
  ::v-deep .add-fragment__add {
    text-align: left;
  }
}
</style>
